import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import type { GuideCategory, GuideEntry, GuideEntryInput } from "@/types/api";

const schema = z
  .object({
    category_id: z.string().min(1, "Pick a category"),
    entry_type: z.enum(["GUIDELINE", "REBUTTAL"]),
    title: z.string().min(1, "Required"),
    title_es: z.string(),
    spiel_text: z.string(),
    spiel_text_es: z.string(),
    internal_note: z.string(),
    internal_note_es: z.string(),
  })
  .refine((v) => v.spiel_text.trim() !== "" || v.internal_note.trim() !== "", {
    message: "Add a spiel, a guidance note, or both — an entry can't be empty.",
    path: ["internal_note"],
  });
type FormValues = z.infer<typeof schema>;

const EMPTY_VALUES: FormValues = {
  category_id: "",
  entry_type: "GUIDELINE",
  title: "",
  title_es: "",
  spiel_text: "",
  spiel_text_es: "",
  internal_note: "",
  internal_note_es: "",
};

interface GuideEntrySheetProps {
  open: boolean;
  onClose: () => void;
  entry: GuideEntry | null;
  categories: GuideCategory[];
  onSubmit: (input: GuideEntryInput) => Promise<void>;
  saving: boolean;
}

/**
 * Create/edit surface for one guide entry — a Sheet rather than a Dialog
 * because the content (title + spiel + internal note, each EN/ES) doesn't
 * fit comfortably in a modal. Field layout deliberately mirrors
 * flow-builder/StepEditorPanel: the internal note gets the same muted
 * warning box treatment since it's the same "agent-only, never read
 * aloud" content, just outside a script.
 */
export function GuideEntrySheet({
  open,
  onClose,
  entry,
  categories,
  onSubmit,
  saving,
}: GuideEntrySheetProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      entry
        ? {
            category_id: entry.category.id,
            entry_type: entry.entry_type,
            title: entry.title,
            title_es: entry.title_es,
            spiel_text: entry.spiel_text,
            spiel_text_es: entry.spiel_text_es,
            internal_note: entry.internal_note,
            internal_note_es: entry.internal_note_es,
          }
        : EMPTY_VALUES
    );
    // form is stable from useForm; only re-sync when the sheet opens for a
    // (possibly different) entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry]);

  const handleSubmit = async (values: FormValues) => {
    await onSubmit(values);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{entry ? "Edit guide entry" : "New guide entry"}</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="mt-6 space-y-5"
            noValidate
          >
            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {categories.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No guide categories yet — create one first.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="entry_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="GUIDELINE">Guideline — general reference</SelectItem>
                      <SelectItem value="REBUTTAL">
                        Rebuttal — exact wording a CSR must say/tell the client
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title / topic (English)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. Caller is yelling and won't calm down" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title_es"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title / topic (Spanish)</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="spiel_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Spiel — what to say (English, optional)</FormLabel>
                  <FormControl>
                    <Textarea rows={4} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="spiel_text_es"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Spiel (Spanish)</FormLabel>
                  <FormControl>
                    <Textarea rows={4} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4 rounded-md border border-warning/30 bg-warning-subtle p-3">
              <FormField
                control={form.control}
                name="internal_note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-warning">
                      Guidance — agent-only, never read to the customer (English)
                    </FormLabel>
                    <FormControl>
                      <Textarea rows={5} className="bg-surface" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="internal_note_es"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-warning">Guidance (Spanish)</FormLabel>
                    <FormControl>
                      <Textarea rows={5} className="bg-surface" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" className="w-full" loading={saving}>
              {entry ? "Save changes" : "Create entry"}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
